import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Progress } from "../../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Briefcase,
  Plus,
  Edit,
  Clock,
  DollarSign,
  CheckCircle,
  Pause,
  Archive,
  Loader2,
  Building2,
  Search,
  PlayCircle,
  CircleDashed,
  PauseCircle,
  CheckCircle2,
  ArchiveRestore,
  History,
  Filter,
  Users,
  UserPlus,
  MessageSquare,
  Timer,
} from "lucide-react";

// Project phases/categories for time tracking
const PROJECT_PHASES = {
  discovery: { name: "Discovery & Assessment" },
  planning: { name: "Planning & Strategy" },
  implementation: { name: "Implementation" },
  training: { name: "Training & Education" },
  review: { name: "Review & Optimization" },
  compliance: { name: "Compliance & Audit" },
  support: { name: "Ongoing Support" },
  documentation: { name: "Documentation" },
  meetings: { name: "Meetings & Calls" },
  research: { name: "Research & Analysis" },
  other: { name: "Other" },
};

// Project status configuration
const PROJECT_STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "bg-gray-100 text-gray-700", icon: CircleDashed },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", icon: PlayCircle },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  on_hold: { label: "On Hold", color: "bg-amber-100 text-amber-700", icon: PauseCircle },
};

const AdminProjects = () => {
  const navigate = useNavigate();
  const { token, API, user, isSuperAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyUsers, setCompanyUsers] = useState({}); // Map of company_id -> users
  const [consultants, setConsultants] = useState([]); // Available consultants (admins)
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active"); // active, archived
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [assignUsersModalOpen, setAssignUsersModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [timeTrackingModalOpen, setTimeTrackingModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [projectUpdates, setProjectUpdates] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    monthly_hours: 40,
    hourly_rate: 150,
    hours_rollover: false,
    company_id: "",
    assigned_users: [],
    lead_consultant_id: "",
    additional_consultants: [],
  });

  // Status change form
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  
  // Update form
  const [updateForm, setUpdateForm] = useState({
    title: "",
    content: "",
    update_type: "progress",
  });
  
  // Time tracking form
  const [timeForm, setTimeForm] = useState({
    hours: "",
    description: "",
    category: "other",
    date: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchCompanies();
    fetchAllProjects();
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Filter for consultant roles (admin, super_admin, consultant)
        const adminUsers = data.filter(u => 
          u.role === "admin" || 
          u.role === "super_admin" || 
          u.role === "consultant"
        );
        setConsultants(adminUsers);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API}/companies/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
        
        // Fetch users for each company
        const usersMap = {};
        await Promise.all(data.map(async (company) => {
          const usersRes = await fetch(`${API}/companies/${company.company_id}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (usersRes.ok) {
            usersMap[company.company_id] = await usersRes.json();
          }
        }));
        setCompanyUsers(usersMap);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchAllProjects = async () => {
    setLoading(true);
    try {
      // Use dedicated endpoint that handles consultant filtering on backend
      const response = await fetch(`${API}/admin/my-projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const projects = await response.json();
        setAllProjects(projects);
      } else {
        setAllProjects([]);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!formData.company_id) {
      toast.error("Please select a company");
      return;
    }
    if (!formData.name.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        name: formData.name,
        description: formData.description,
        monthly_hours: formData.monthly_hours.toString(),
        hourly_rate: formData.hourly_rate.toString(),
      });
      
      // Add assigned users if any
      if (formData.assigned_users.length > 0) {
        params.append("assigned_users", formData.assigned_users.join(","));
      }
      
      const response = await fetch(
        `${API}/admin/companies/${formData.company_id}/projects?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success("Project created successfully");
        fetchAllProjects();
        setCreateModalOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create project");
      }
    } catch (error) {
      toast.error("Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        name: formData.name,
        description: formData.description,
        monthly_hours: formData.monthly_hours.toString(),
        hourly_rate: formData.hourly_rate.toString(),
        hours_rollover: formData.hours_rollover.toString(),
      });
      
      // Add company_id if changed
      if (formData.company_id && formData.company_id !== selectedProject.company_id) {
        params.append("company_id", formData.company_id);
      }
      
      // Add consultant assignments
      if (formData.lead_consultant_id) {
        params.append("lead_consultant_id", formData.lead_consultant_id);
      }
      params.append("additional_consultant_ids", formData.additional_consultants.join(","));
      
      const response = await fetch(
        `${API}/admin/projects/${selectedProject.project_id}?${params.toString()}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        // Also update assigned users if changed
        const originalAssigned = selectedProject.assigned_users || [];
        const newAssigned = formData.assigned_users || [];
        const assignedChanged = JSON.stringify(originalAssigned.sort()) !== JSON.stringify(newAssigned.sort());
        
        if (assignedChanged) {
          await fetch(
            `${API}/admin/projects/${selectedProject.project_id}/assigned-users?user_ids=${newAssigned.join(",")}`,
            {
              method: "PUT",
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        }
        
        toast.success("Project updated successfully");
        fetchAllProjects();
        setEditModalOpen(false);
        setSelectedProject(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update project");
      }
    } catch (error) {
      toast.error("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedProject || !newStatus) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append("new_status", newStatus);
      if (statusNote) params.append("note", statusNote);

      const response = await fetch(
        `${API}/admin/projects/${selectedProject.project_id}/status?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success("Project status updated");
        fetchAllProjects();
        fetchStatusHistory(selectedProject.project_id);
        setNewStatus("");
        setStatusNote("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveProject = async (project) => {
    const isArchived = project.status === "archived";
    const action = isArchived ? "restore" : "archive";
    
    if (!window.confirm(`Are you sure you want to ${action} this project?`)) return;
    
    try {
      const newStatus = isArchived ? "active" : "archived";
      const response = await fetch(
        `${API}/admin/projects/${project.project_id}?status=${newStatus}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success(`Project ${action}d successfully`);
        fetchAllProjects();
      } else {
        toast.error(`Failed to ${action} project`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} project`);
    }
  };

  const fetchStatusHistory = async (projectId) => {
    try {
      const response = await fetch(`${API}/admin/projects/${projectId}/status-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStatusHistory(data.history || []);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setFormData({
      name: project.name || "",
      description: project.description || "",
      monthly_hours: project.monthly_hours || 40,
      hourly_rate: project.hourly_rate || 150,
      hours_rollover: project.hours_rollover || false,
      company_id: project.company_id || "",
      assigned_users: project.assigned_users || [],
      lead_consultant_id: project.lead_consultant_id || "",  // Only use explicit lead_consultant_id, not fallback
      additional_consultants: project.additional_consultants || [],
    });
    setEditModalOpen(true);
  };

  const openStatusModal = (project) => {
    setSelectedProject(project);
    setNewStatus(project.project_status || "not_started");
    setStatusNote("");
    fetchStatusHistory(project.project_id);
    setStatusModalOpen(true);
  };

  const openAssignUsersModal = (project) => {
    setSelectedProject(project);
    setFormData(prev => ({
      ...prev,
      assigned_users: project.assigned_users || [],
    }));
    setAssignUsersModalOpen(true);
  };

  const openUpdateModal = (project) => {
    setSelectedProject(project);
    setUpdateForm({
      title: "",
      content: "",
      update_type: "progress",
    });
    fetchProjectUpdates(project.project_id);
    setUpdateModalOpen(true);
  };

  const fetchProjectUpdates = async (projectId) => {
    try {
      const response = await fetch(`${API}/admin/projects/${projectId}/updates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProjectUpdates(data || []);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const handleAddUpdate = async () => {
    if (!selectedProject || !updateForm.title.trim()) {
      toast.error("Please enter a title for the update");
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        title: updateForm.title,
        content: updateForm.content,
        update_type: updateForm.update_type,
      });
      
      const response = await fetch(
        `${API}/admin/projects/${selectedProject.project_id}/updates?${params.toString()}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success("Update posted successfully");
        setUpdateForm({ title: "", content: "", update_type: "progress" });
        fetchProjectUpdates(selectedProject.project_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to post update");
      }
    } catch (error) {
      toast.error("Failed to post update");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAssignedUsers = async () => {
    if (!selectedProject) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API}/admin/projects/${selectedProject.project_id}/assigned-users?user_ids=${formData.assigned_users.join(",")}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (response.ok) {
        toast.success("Assigned users updated");
        fetchAllProjects();
        setAssignUsersModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update assigned users");
      }
    } catch (error) {
      toast.error("Failed to update assigned users");
    } finally {
      setSaving(false);
    }
  };

  const toggleUserAssignment = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId],
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      monthly_hours: 40,
      hourly_rate: 150,
      hours_rollover: false,
      company_id: "",
      assigned_users: [],
      lead_consultant_id: "",
      additional_consultants: [],
    });
  };
  
  // Time Tracking Functions
  const openTimeTrackingModal = (project) => {
    setSelectedProject(project);
    setTimeForm({
      hours: "",
      description: "",
      category: "other",
      date: new Date().toISOString().split("T")[0],
    });
    setTimeTrackingModalOpen(true);
  };
  
  const handleAddTimeEntry = async () => {
    if (!timeForm.hours || parseFloat(timeForm.hours) <= 0) {
      toast.error("Please enter valid hours");
      return;
    }
    if (!timeForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        project_id: selectedProject.project_id,
        hours: timeForm.hours,
        description: timeForm.description,
        category: timeForm.category,
        date: timeForm.date,
      });
      
      const response = await fetch(`${API}/admin/time-entries?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Time entry added successfully");
        fetchAllProjects();
        setTimeTrackingModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add time entry");
      }
    } catch (error) {
      toast.error("Failed to add time entry");
    } finally {
      setSaving(false);
    }
  };

  // Filter projects
  const filteredProjects = allProjects.filter((project) => {
    // Tab filter (active vs archived)
    if (activeTab === "active" && project.status === "archived") return false;
    if (activeTab === "archived" && project.status !== "archived") return false;
    
    // Search filter
    const search = searchQuery.toLowerCase();
    const matchesSearch = 
      project.name?.toLowerCase().includes(search) ||
      project.description?.toLowerCase().includes(search) ||
      project.company_name?.toLowerCase().includes(search);
    if (!matchesSearch) return false;
    
    // Status filter
    if (statusFilter !== "all" && project.project_status !== statusFilter) return false;
    
    // Company filter
    if (companyFilter !== "all" && project.company_id !== companyFilter) return false;
    
    return true;
  });

  const getStatusBadge = (projectStatus) => {
    const config = PROJECT_STATUS_CONFIG[projectStatus] || PROJECT_STATUS_CONFIG.not_started;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getProjectStats = () => {
    const active = allProjects.filter(p => p.status !== "archived");
    return {
      total: active.length,
      not_started: active.filter(p => p.project_status === "not_started" || !p.project_status).length,
      in_progress: active.filter(p => p.project_status === "in_progress").length,
      completed: active.filter(p => p.project_status === "completed").length,
      on_hold: active.filter(p => p.project_status === "on_hold").length,
      archived: allProjects.filter(p => p.status === "archived").length,
    };
  };

  const stats = getProjectStats();

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight">Projects</h1>
            <p className="text-sm text-gray-400 mt-1">Manage all projects across companies</p>
          </div>
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="bg-rnb-navy hover:bg-rnb-navy/90 gap-1.5 text-xs h-9"
            size="sm"
            data-testid="create-project-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            New Project
          </Button>
        </div>

        {/* Stats — colorful, inline */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Total Active", value: stats.total, gradient: "from-rnb-navy to-rnb-blue", icon: Briefcase },
            { label: "Not Started", value: stats.not_started, gradient: "from-gray-400 to-gray-500", icon: CircleDashed },
            { label: "In Progress", value: stats.in_progress, gradient: "from-rnb-blue to-sky-500", icon: PlayCircle },
            { label: "Completed", value: stats.completed, gradient: "from-emerald-500 to-rnb-teal", icon: CheckCircle2 },
            { label: "On Hold", value: stats.on_hold, gradient: "from-amber-400 to-orange-500", icon: PauseCircle },
            { label: "Archived", value: stats.archived, gradient: "from-gray-300 to-gray-400", icon: ArchiveRestore },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 flex flex-col items-center gap-2">
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${s.gradient} flex items-center justify-center`}>
                <s.icon className="w-4 h-4 text-white" />
              </div>
              <p className="font-accent text-xl font-bold text-rnb-navy">{s.value}</p>
              <p className="text-[11px] text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs & Filters — less boxy */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <TabsList className="bg-white border border-gray-200 rounded-xl p-1">
              <TabsTrigger value="active" data-testid="tab-active" className="rounded-lg text-xs">
                Active Projects
              </TabsTrigger>
              <TabsTrigger value="archived" data-testid="tab-archived" className="rounded-lg text-xs">
                Archived ({stats.archived})
              </TabsTrigger>
            </TabsList>
            
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rnb-blue/20 focus:border-rnb-blue/30 transition-all"
                />
              </div>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-[180px] rounded-xl border-gray-200">
                  <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.company_id} value={company.company_id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] rounded-xl border-gray-200">
                  <Filter className="w-4 h-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(PROJECT_STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value="active" className="mt-0">
            <ProjectsGrid 
              projects={filteredProjects}
              loading={loading}
              navigate={navigate}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>
          
          <TabsContent value="archived" className="mt-0">
            <ProjectsGrid 
              projects={filteredProjects}
              loading={loading}
              navigate={navigate}
              getStatusBadge={getStatusBadge}
              isArchived
            />
          </TabsContent>
        </Tabs>

        {/* Create Project Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Company *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger data-testid="company-select">
                    <SelectValue placeholder="Select a company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.company_id} value={company.company_id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Website Redesign"
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief project description..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monthly Hours</Label>
                  <Input
                    type="number"
                    value={formData.monthly_hours}
                    onChange={(e) => setFormData({ ...formData, monthly_hours: parseInt(e.target.value) || 0 })}
                    min="1"
                  />
                </div>
                {/* Only show hourly rate field to super admins */}
                {isSuperAdmin && (
                  <div>
                    <Label>Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
              </div>
              
              {/* Assign Users Section */}
              {formData.company_id && companyUsers[formData.company_id]?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Assign Clients (optional)</Label>
                  <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                    {companyUsers[formData.company_id]?.map((user) => (
                      <label
                        key={user.user_id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assigned_users.includes(user.user_id)}
                          onChange={() => toggleUserAssignment(user.user_id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm flex-1">{user.name || user.email}</span>
                        {user.is_company_admin && (
                          <Badge variant="secondary" className="text-[10px] px-1.5">Admin</Badge>
                        )}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.assigned_users.length} client(s) will have access
                  </p>
                </div>
              )}
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateProject} 
                disabled={saving}
                className="bg-[#2471A3] hover:bg-[#1A5276]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Project Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Company Selector */}
              <div>
                <Label>Company *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) => {
                    setFormData({ 
                      ...formData, 
                      company_id: value,
                      assigned_users: [] // Reset assigned users when company changes
                    });
                  }}
                >
                  <SelectTrigger data-testid="edit-company-select">
                    <SelectValue placeholder="Select a company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.company_id} value={company.company_id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Project Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="edit-project-name"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monthly Hours</Label>
                  <Input
                    type="number"
                    value={formData.monthly_hours}
                    onChange={(e) => setFormData({ ...formData, monthly_hours: parseInt(e.target.value) || 0 })}
                  />
                </div>
                {/* Only show hourly rate field to super admins */}
                {isSuperAdmin && (
                  <div>
                    <Label>Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.hours_rollover}
                  onCheckedChange={(checked) => setFormData({ ...formData, hours_rollover: checked })}
                />
                <Label>Allow hours rollover to next month</Label>
              </div>
              
              {/* Consultant Assignment Section - Super Admin Only */}
              {isSuperAdmin && (
                <div className="border-t pt-4">
                  <Label className="mb-2 block flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-[#2471A3]" />
                    Assign Consultants
                  </Label>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-500">Lead Consultant (shown to client)</Label>
                      <Select
                        value={formData.lead_consultant_id || "none"}
                        onValueChange={(value) => setFormData({ ...formData, lead_consultant_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select lead consultant..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="text-gray-500">No lead consultant assigned</span>
                          </SelectItem>
                          {consultants.map((consultant) => (
                            <SelectItem key={consultant.user_id} value={consultant.user_id}>
                              {consultant.name || consultant.email}
                              {consultant.role === "super_admin" && " (Super Admin)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">Select "No lead consultant assigned" to remove from client dashboard</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Additional Consultants (can track time)</Label>
                      <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                        {consultants
                          .filter(c => c.user_id !== formData.lead_consultant_id)
                          .map((consultant) => (
                          <label
                            key={consultant.user_id}
                            className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.additional_consultants.includes(consultant.user_id)}
                              onChange={() => {
                                const updated = formData.additional_consultants.includes(consultant.user_id)
                                  ? formData.additional_consultants.filter(id => id !== consultant.user_id)
                                  : [...formData.additional_consultants, consultant.user_id];
                                setFormData({ ...formData, additional_consultants: updated });
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm flex-1">{consultant.name || consultant.email}</span>
                            {consultant.role === "super_admin" && (
                              <Badge variant="secondary" className="text-[10px] px-1.5">Super</Badge>
                            )}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.additional_consultants.length} additional consultant(s) can track time
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Assign Clients Section */}
              <div className="border-t pt-4">
                <Label className="mb-2 block flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#2471A3]" />
                  Assign Clients
                </Label>
                {formData.company_id && companyUsers[formData.company_id]?.length > 0 ? (
                  <>
                    <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                      {companyUsers[formData.company_id]?.map((user) => (
                        <label
                          key={user.user_id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.assigned_users.includes(user.user_id)}
                            onChange={() => toggleUserAssignment(user.user_id)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm flex-1">{user.name || user.email}</span>
                          {user.is_company_admin && (
                            <Badge variant="secondary" className="text-[10px] px-1.5">Admin</Badge>
                          )}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.assigned_users.length} client(s) will have access to this project
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                    {formData.company_id 
                      ? "No clients in this company yet" 
                      : "Select a company to assign clients"}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateProject} 
                disabled={saving}
                className="bg-[#2471A3] hover:bg-[#1A5276]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Change Modal */}
        <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-[#2471A3]" />
                Project Status: {selectedProject?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Current Status */}
              <div>
                <Label className="mb-2 block">Current Status</Label>
                <div className="flex items-center gap-2">
                  {selectedProject && getStatusBadge(selectedProject.project_status)}
                </div>
              </div>

              {/* Change Status */}
              <div>
                <Label className="mb-2 block">Change Status To</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PROJECT_STATUS_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    const isSelected = newStatus === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setNewStatus(key)}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                          isSelected
                            ? "border-[#2471A3] bg-[#2471A3]/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${isSelected ? "text-[#2471A3]" : "text-gray-500"}`} />
                        <span className={`text-sm ${isSelected ? "text-[#2471A3] font-medium" : "text-gray-600"}`}>
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Note */}
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Add context for this status change..."
                  rows={2}
                />
              </div>

              {/* Update Button */}
              <Button
                onClick={handleStatusChange}
                disabled={saving || newStatus === selectedProject?.project_status}
                className="w-full bg-[#2471A3] hover:bg-[#1A5276]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update Status
              </Button>

              {/* Status History */}
              <div className="border-t pt-4">
                <Label className="mb-2 block">Status History</Label>
                {statusHistory.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {statusHistory.map((entry, idx) => {
                      const config = PROJECT_STATUS_CONFIG[entry.new_status] || PROJECT_STATUS_CONFIG.not_started;
                      return (
                        <div key={entry.history_id || idx} className="flex gap-3 text-sm">
                          <div className="flex flex-col items-center">
                            <div className={`w-2.5 h-2.5 rounded-full ${config.color.split(' ')[0]}`} />
                            {idx < statusHistory.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                          </div>
                          <div className="flex-1 pb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{config.label}</span>
                              <span className="text-gray-400 text-xs">
                                {new Date(entry.changed_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">by {entry.changed_by_name}</p>
                            {entry.note && (
                              <p className="text-xs text-gray-600 mt-1 bg-gray-50 p-1.5 rounded">{entry.note}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-2">No status changes yet</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Users Modal */}
        <Dialog open={assignUsersModalOpen} onOpenChange={setAssignUsersModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#2471A3]" />
                Assign Clients: {selectedProject?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-gray-600">
                Select which clients can access this project. They will see the project in their portal.
              </p>
              
              {selectedProject && companyUsers[selectedProject.company_id]?.length > 0 ? (
                <div className="border rounded-lg p-2 max-h-64 overflow-y-auto space-y-1">
                  {companyUsers[selectedProject.company_id]?.map((user) => (
                    <label
                      key={user.user_id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer border border-transparent hover:border-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={formData.assigned_users.includes(user.user_id)}
                        onChange={() => toggleUserAssignment(user.user_id)}
                        className="rounded border-gray-300 w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{user.name || "Unnamed User"}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      {user.is_company_admin && (
                        <Badge variant="secondary" className="text-[10px]">Company Admin</Badge>
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>No users in this company</p>
                  <p className="text-xs mt-1">Add users to the company first</p>
                </div>
              )}
              
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <strong>{formData.assigned_users.length}</strong> client(s) selected
              </p>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setAssignUsersModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateAssignedUsers}
                disabled={saving}
                className="bg-[#2471A3] hover:bg-[#1A5276]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Assignments
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Project Update Modal */}
        <Dialog open={updateModalOpen} onOpenChange={setUpdateModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Post Project Update</DialogTitle>
              <DialogDescription>
                Share a status update for {selectedProject?.name}. This will be visible to clients.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="update-title">Title *</Label>
                <Input
                  id="update-title"
                  value={updateForm.title}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Weekly Progress Update"
                  data-testid="update-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="update-type">Update Type</Label>
                <Select 
                  value={updateForm.update_type} 
                  onValueChange={(v) => setUpdateForm(prev => ({ ...prev, update_type: v }))}
                >
                  <SelectTrigger data-testid="update-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="progress">Progress Update</SelectItem>
                    <SelectItem value="milestone">Milestone Achieved</SelectItem>
                    <SelectItem value="blocker">Blocker/Issue</SelectItem>
                    <SelectItem value="note">General Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="update-content">Details</Label>
                <Textarea
                  id="update-content"
                  value={updateForm.content}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Provide details about this update..."
                  rows={4}
                  data-testid="update-content-input"
                />
              </div>
              
              {/* Previous Updates */}
              {projectUpdates.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-sm mb-3 text-gray-700">Recent Updates</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {projectUpdates.slice(0, 5).map((update) => (
                      <div key={update.update_id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{update.title}</span>
                          <Badge variant="outline" className="text-xs">
                            {update.update_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{update.content}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(update.created_at).toLocaleDateString()} • {update.created_by_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUpdateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddUpdate}
                disabled={saving || !updateForm.title.trim()}
                className="bg-[#1FC6B7] hover:bg-[#1FC6B7]/90"
                data-testid="post-update-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Post Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Time Tracking Modal */}
        <Dialog open={timeTrackingModalOpen} onOpenChange={setTimeTrackingModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Timer className="w-5 h-5 text-[#2471A3]" />
                Track Time
              </DialogTitle>
              <DialogDescription>
                Log time for {selectedProject?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="time-hours">Hours *</Label>
                  <Input
                    id="time-hours"
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={timeForm.hours}
                    onChange={(e) => setTimeForm(prev => ({ ...prev, hours: e.target.value }))}
                    placeholder="e.g., 2.5"
                    data-testid="time-hours-input"
                  />
                </div>
                <div>
                  <Label htmlFor="time-date">Date</Label>
                  <Input
                    id="time-date"
                    type="date"
                    value={timeForm.date}
                    onChange={(e) => setTimeForm(prev => ({ ...prev, date: e.target.value }))}
                    data-testid="time-date-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="time-category">Category</Label>
                <Select
                  value={timeForm.category}
                  onValueChange={(value) => setTimeForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="time-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PROJECT_PHASES).map(([key, { name }]) => (
                      <SelectItem key={key} value={key}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="time-description">Description *</Label>
                <Textarea
                  id="time-description"
                  value={timeForm.description}
                  onChange={(e) => setTimeForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What did you work on?"
                  rows={3}
                  data-testid="time-description-input"
                />
              </div>
              
              {/* Hours Info */}
              {selectedProject && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Hours used this month:</span>
                    <span className="font-medium">{selectedProject.hours_used?.toFixed(1) || 0} hrs</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Hours remaining:</span>
                    <span className="font-medium text-green-600">{selectedProject.hours_remaining?.toFixed(1) || selectedProject.monthly_hours} hrs</span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTimeTrackingModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddTimeEntry}
                disabled={saving || !timeForm.hours || !timeForm.description.trim()}
                className="bg-[#2471A3] hover:bg-[#2471A3]/90"
                data-testid="save-time-entry-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Time Entry
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

// Projects Grid Component
const ProjectsGrid = ({ projects, loading, navigate, getStatusBadge, isArchived = false }) => {
  const { isSuperAdmin } = useAuth();
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#2471A3]" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">
          {isArchived ? "No archived projects" : "No projects found"}
        </p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {projects.map((project) => {
        const hoursUsed = project.hours_used || 0;
        const hoursRemaining = project.hours_remaining || project.monthly_hours;
        const leadName = project.lead_consultant?.name || project.lead_consultant?.email;
        
        return (
          <Card 
            key={project.project_id} 
            className={`hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-[#2471A3]/20 ${isArchived ? "opacity-75" : ""}`}
            onClick={() => navigate(`/admin/projects/${project.project_id}`)}
            data-testid={`project-card-${project.project_id}`}
          >
            <CardContent className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#1A2636] truncate">{project.name}</h3>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3" />
                    {project.company_name}
                  </p>
                </div>
                {getStatusBadge(project.project_status)}
              </div>
              
              {/* Description */}
              {project.description && (
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{project.description}</p>
              )}
              
              {/* Stats Row */}
              <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {project.monthly_hours} hrs/mo
                </span>
                {/* Only show hourly rate to super admins */}
                {isSuperAdmin && (
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    ${project.hourly_rate}/hr
                  </span>
                )}
              </div>

              {/* Lead Consultant */}
              <div className="flex items-center gap-2 mb-3 text-xs">
                <Users className="w-3 h-3 text-gray-400" />
                {leadName ? (
                  <span className="text-gray-600">{leadName}</span>
                ) : (
                  <span className="text-gray-400 italic">No consultant assigned</span>
                )}
              </div>

              {/* Hours Progress */}
              <div className="pt-3 border-t">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{hoursUsed.toFixed(1)} hrs used</span>
                  <span className="text-green-600 font-medium">{hoursRemaining.toFixed(1)} remaining</span>
                </div>
                <Progress 
                  value={Math.min((hoursUsed / project.monthly_hours) * 100, 100)} 
                  className="h-1.5"
                />
              </div>
              
              {/* Click hint */}
              <p className="text-xs text-center text-gray-400 mt-3">Click to view details</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminProjects;
